import { useEffect, useState } from 'react';
import { listAttendanceEventsToday, listEmployees, listLeaveRequests, listShiftSwaps } from '../../api/hr';
import { listStockBalances } from '../../api/inventory';
import { listItems } from '../../api/masterData';
import { listWasteEvents } from '../../api/inventoryOps';
import { describeActivity, listAuditEvents, type AuditEventDetail } from '../../api/activity';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { DashboardSkeleton, EmptyState, ErrorNote, KPICard, SectionCard, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconAlertTriangle, IconCalendar, IconRepeat, IconShield, IconUsers } from '../../components/icons';
import type { AttendanceEvent, Employee, Item, LeaveRequest, ShiftSwap, StockBalance, WasteEvent } from '../../types/domain';

/**
 * Leader workspace — the narrowest, shift-level operational role. Scope
 * is deliberately smaller than Supervisor: today's team, attendance, and
 * pending shift-handover items only. Every panel reads a real table this
 * role already has (attendance_event/leave_request/shift_swap.read per
 * migration 0036); no lateness metric, task checklist, or handover
 * feature is fabricated since none of those have a backend yet — the
 * workspace is honest about being narrower than the future design, not
 * padded out with invented numbers.
 */
export default function LeaderDashboard() {
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [attendanceToday, setAttendanceToday] = useState<AttendanceEvent[] | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[] | null>(null);
  const [shiftSwaps, setShiftSwaps] = useState<ShiftSwap[] | null>(null);
  const [events, setEvents] = useState<AuditEventDetail[] | null>(null);
  const [balances, setBalances] = useState<StockBalance[] | null>(null);
  const [wasteEvents, setWasteEvents] = useState<WasteEvent[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canReadEmployee = hasPermission('employee', 'read');
  const canReadAttendance = hasPermission('attendance_event', 'read');
  const canReadLeave = hasPermission('leave_request', 'read');
  const canReadShiftSwap = hasPermission('shift_swap', 'read');
  const canReadBalance = hasPermission('stock_balance', 'read');
  const canReadWaste = hasPermission('waste', 'read');
  const canReadItem = hasPermission('item', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadEmployee ? listEmployees(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadAttendance ? listAttendanceEventsToday(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadLeave ? listLeaveRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadShiftSwap ? listShiftSwaps(activeBranchId ?? undefined) : Promise.resolve(null),
      listAuditEvents(10),
      canReadBalance ? listStockBalances(undefined, activeBranchId ? activeBranchWarehouseIds : undefined) : Promise.resolve(null),
      canReadWaste ? listWasteEvents(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadItem ? listItems() : Promise.resolve([]),
    ])
      .then(([emp, att, leave, swap, ev, bal, waste, itemRows]) => {
        if (cancelled) return;
        setEmployees(emp);
        setAttendanceToday(att);
        setLeaveRequests(leave);
        setShiftSwaps(swap);
        setEvents(ev);
        setBalances(bal);
        setWasteEvents(waste);
        setItems(itemRows);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadEmployee, canReadAttendance, canReadLeave, canReadShiftSwap, canReadBalance, canReadWaste, canReadItem, activeBranchId, activeBranchWarehouseIds.join(',')]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const hadirCount = attendanceToday ? new Set(attendanceToday.filter((a) => a.event_type === 'CLOCK_IN').map((a) => a.employee_id)).size : null;
  const pendingLeave = leaveRequests ? leaveRequests.filter((l) => l.status === 'REQUESTED') : [];
  const pendingSwap = shiftSwaps ? shiftSwaps.filter((s) => s.status === 'REQUESTED') : [];
  const stockOut = balances ? balances.filter((b) => b.qty_on_hand <= 0) : [];
  const draftWaste = wasteEvents ? wasteEvents.filter((w) => w.status === 'DRAFT') : [];
  const employeeName = (id: string) => employees?.find((e) => e.employee_id === id)?.legal_name ?? 'Karyawan';
  const itemLabel = (id: string) => items.find((it) => it.item_id === id)?.item_code ?? id.slice(0, 8);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        <KPICard label="Anggota Tim" value={canReadEmployee ? (employees?.length ?? 0) : '—'} hint={canReadEmployee ? undefined : 'Tanpa izin'} icon={<IconUsers size={16} />} tone="primary" />
        <KPICard label="Hadir Hari Ini" value={canReadAttendance ? (hadirCount ?? 0) : '—'} hint={canReadAttendance ? undefined : 'Tanpa izin'} icon={<IconShield size={16} />} tone="success" />
        <KPICard label="Cuti Pending" value={canReadLeave ? pendingLeave.length : '—'} hint={canReadLeave ? undefined : 'Tanpa izin'} icon={<IconCalendar size={16} />} tone="info" />
        <KPICard label="Tukar Shift Pending" value={canReadShiftSwap ? pendingSwap.length : '—'} hint={canReadShiftSwap ? undefined : 'Tanpa izin'} icon={<IconRepeat size={16} />} tone="info" />
        {canReadBalance && <KPICard label="SKU Stok Habis" value={stockOut.length} icon={<IconAlertTriangle size={16} />} tone={stockOut.length > 0 ? 'danger' : 'success'} />}
      </div>

      <div className="alex-grid-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Tim Shift Ini">
            {!canReadEmployee && <EmptyState text="Tanpa izin employee.read." variant="compact" />}
            {canReadEmployee && (!employees || employees.length === 0) && <EmptyState text="Belum ada anggota tim tercatat." variant="compact" />}
            {canReadEmployee && employees && employees.length > 0 && (
              <table style={tableStyle}>
                <tbody>
                  {employees.slice(0, 8).map((emp) => (
                    <tr key={emp.employee_id}>
                      <td style={tdStyle}>{emp.legal_name}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={emp.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <SectionCard title="Aktivitas Terbaru">
            {!events || events.length === 0 ? (
              <EmptyState text="Belum ada aktivitas tercatat." variant="table" />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Waktu</th>
                    <th style={thStyle}>Kejadian</th>
                    <th style={thStyle}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice(0, 6).map((e) => (
                    <tr key={e.event_id}>
                      <td style={tdStyle}>{new Date(e.occurred_at).toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>{describeActivity(e)}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={e.action} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Serah Terima & Cuti Pending">
            {!canReadShiftSwap && !canReadLeave && <EmptyState text="Tanpa izin." variant="compact" />}
            {pendingSwap.length === 0 && pendingLeave.length === 0 && <EmptyState text="Tidak ada serah terima atau cuti pending." variant="compact" />}
            {(pendingSwap.length > 0 || pendingLeave.length > 0) && (
              <table style={tableStyle}>
                <tbody>
                  {pendingSwap.slice(0, 4).map((s) => (
                    <tr key={s.swap_id}>
                      <td style={tdStyle}>Tukar shift · {employeeName(s.requesting_employee_id)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={s.status} />
                      </td>
                    </tr>
                  ))}
                  {pendingLeave.slice(0, 4).map((l) => (
                    <tr key={l.leave_id}>
                      <td style={tdStyle}>Cuti {l.leave_type} · {l.date_from}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={l.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          {(canReadBalance || canReadWaste) && (
            <SectionCard title="Isu Stok Area Ini">
              {stockOut.length === 0 && draftWaste.length === 0 ? (
                <EmptyState text="Tidak ada isu stok tercatat." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {stockOut.slice(0, 3).map((b) => (
                      <tr key={`${b.warehouse_id}-${b.item_id}`}>
                        <td style={tdStyle}>{itemLabel(b.item_id)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--alex-danger)', fontWeight: 600 }}>Habis</td>
                      </tr>
                    ))}
                    {draftWaste.slice(0, 3).map((w) => (
                      <tr key={w.waste_id}>
                        <td style={tdStyle}>{w.waste_number}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <StatusBadge status={w.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
